import React from 'react';
import {
  cureElectionAsked,
  getContingencyLabelWithDeadline,
  getCureElectionSummary,
  getFinancingTermsSummary,
  getLeadPaintElectionSummary,
  getSummaryContingencyKeys,
  getTitlePeriodsSummary,
} from '../../contingencyCatalog';
import { formatDateOnly, formatPrice, parseDateOnly } from '../../utils';
import { deadlineTimeLabel } from '../../offerExpiration';
import { LEGAL_DISCLAIMER } from '../../../../utils/disclaimers';
import './ReviewStep.css';

function Row({ label, value, meta, onClick }) {
  return (
    <button type="button" className="review-step__row" onClick={onClick}>
      <span className="review-step__row-label">{label}</span>
      <span className="review-step__row-value">
        <span className="review-step__row-value-text">{value}</span>
        {meta && <span className="review-step__row-meta">{meta}</span>}
      </span>
      <span className="review-step__edit" aria-hidden="true">Edit ›</span>
    </button>
  );
}

export default function ReviewStep({ wiz, property, onJumpToStep }) {
  const list = property?.price || property?.list_price || 0;
  const d = wiz.derived;
  const activeKeys = getSummaryContingencyKeys(wiz.form.contingencies);
  // The lead-paint election's own line (WI.offer.lead_paint.summary.*), only
  // where the election is asked.
  const leadPaintSummary = wiz.leadPaintMode ? getLeadPaintElectionSummary(wiz.form.contingencies) : null;
  // The cure election's line (WI.offer.cure.summary.*), only where it is asked.
  const cureSummary = cureElectionAsked(wiz.cureCategory, wiz.form.contingencies)
    ? getCureElectionSummary(wiz.form.contingencies)
    : null;
  const contingencyLabels = activeKeys.map((k) => getContingencyLabelWithDeadline(k, wiz.form.contingencies));
  // The loan terms and title periods (WI.offer.financing_terms.summary,
  // WI.offer.title_periods.summary), only on a WI offer.
  const financingSummary = wiz.termsCategory
    ? getFinancingTermsSummary(wiz.form.contingencies, d.loanAmount)
    : null;
  const titleSummary = wiz.termsCategory ? getTitlePeriodsSummary(wiz.form.contingencies) : null;

  let priceTone = 'neutral';
  let priceLabel = 'Matches asking price';
  if (list && d.offerPrice) {
    if (d.deltaPct > 0.01) {
      priceTone = 'high';
      priceLabel = `${d.deltaPct.toFixed(1)}% above asking`;
    } else if (d.deltaPct < -0.01) {
      priceTone = 'low';
      priceLabel = `${Math.abs(d.deltaPct).toFixed(1)}% below asking`;
    }
  }

  let closingDays = null;
  if (wiz.form.closing_date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // parseDateOnly, not `new Date(...)`: a bare YYYY-MM-DD parses as UTC
    // midnight, which skews this day count against a local-midnight `today`.
    const target = parseDateOnly(wiz.form.closing_date);
    closingDays = target ? Math.round((target - today) / 86400000) : null;
  }

  return (
    <section className="review-step">
      <header className="review-step__intro">
        <h3>Review your offer</h3>
        <p>Confirm the details below. Click any row to jump back and edit.</p>
      </header>

      <div className="review-step__list">
        <Row
          label="Offer price"
          value={
            <>
              {formatPrice(d.offerPrice)}{' '}
              {list > 0 && (
                <span className={`review-step__pill review-step__pill--${priceTone}`}>{priceLabel}</span>
              )}
            </>
          }
          onClick={() => onJumpToStep(0)}
        />
        <Row
          label="Down payment"
          value={formatPrice(d.dpAmount)}
          meta={`${d.dpPct}%`}
          onClick={() => onJumpToStep(1)}
        />
        <Row
          label="Financed amount"
          value={formatPrice(d.financedAmount)}
          meta={d.monthlyPI > 0 ? `≈ ${formatPrice(d.monthlyPI)}/mo` : null}
          onClick={() => onJumpToStep(1)}
        />
        <Row
          label="Earnest money"
          value={d.earnest > 0 ? formatPrice(d.earnest) : 'Not included'}
          onClick={() => onJumpToStep(1)}
        />
        <Row
          label="Closing date"
          value={wiz.form.closing_date || '—'}
          meta={closingDays != null ? `${closingDays} days from today` : null}
          onClick={() => onJumpToStep(2)}
        />
        <Row
          label="Offer deadline"
          value={formatDateOnly(wiz.form.expires_on)}
          meta={
            wiz.form.expires_on
              ? `${deadlineTimeLabel(wiz.form.expires_on, property?.timezone)} (property's local time)`
              : null
          }
          onClick={() => onJumpToStep(2)}
        />
        <Row
          label="Contingencies"
          value={
            contingencyLabels.length === 0 && !leadPaintSummary ? (
              'None — non-contingent offer'
            ) : (
              <>
                {contingencyLabels.join(', ')}
                {leadPaintSummary && (
                  <span className="review-step__lead-paint" data-testid="review-lead-paint">
                    {leadPaintSummary}
                  </span>
                )}
                {cureSummary && (
                  <span className="review-step__cure" data-testid="review-cure">
                    {cureSummary}
                  </span>
                )}
              </>
            )
          }
          onClick={() => onJumpToStep(2)}
        />
        {financingSummary && (
          <Row
            label="Loan terms"
            value={<span data-testid="review-financing-terms">{financingSummary}</span>}
            onClick={() => onJumpToStep(2)}
          />
        )}
        {titleSummary && (
          <Row
            label="Title"
            value={<span data-testid="review-title-periods">{titleSummary}</span>}
            onClick={() => onJumpToStep(2)}
          />
        )}
        <Row
          label="Additional terms"
          value={wiz.form.additional_terms ? wiz.form.additional_terms : 'None'}
          onClick={() => onJumpToStep(2)}
        />
      </div>

      <p className="review-step__ready">Ready to submit? Use the button below.</p>
      <p className="review-step__disclaimer">{LEGAL_DISCLAIMER}</p>
    </section>
  );
}
